import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppUser } from '../../../hooks/useAppUser';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import { useApi } from '../../../hooks/useApi';

const ROLES = [
  { value: 'org-admin', label: 'Admin', description: 'Full access except billing' },
  { value: 'org-developer', label: 'Developer', description: 'API access and monitoring' },
  { value: 'org-user', label: 'Viewer', description: 'View-only access' },
];

function getRoleLabel(role) {
  const found = ROLES.find(r => r.value === role);
  return found?.label || role;
}

function getRoleColor(role) {
  switch (role) {
    case 'org-owner':
      return 'primary';
    case 'org-admin':
      return 'secondary';
    case 'org-developer':
      return 'info';
    default:
      return 'default';
  }
}

function TeamManagement() {
  const queryClient = useQueryClient();
  const api = useApi();
  const { user } = useAppUser();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org-developer');
  const [error, setError] = useState(null);

  // Include user ID in query keys so cache is per-user
  const userId = user?.id || user?._id || user?.email;

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['members', userId],
    queryFn: () => api.get('/api/members'),
    enabled: !!userId,
  });

  const inviteMutation = useMutation({
    mutationFn: (data) => api.post('/api/members/invite', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('org-developer');
      setError(null);
    },
    onError: (err) => {
      setError(err.message || 'Failed to send invitation');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }) =>
      api.patch(`/api/members/${userId}/role`, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      setEditDialogOpen(false);
      setSelectedMember(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId) => api.delete(`/api/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
      setDeleteDialogOpen(false);
      setSelectedMember(null);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId) => api.delete(`/api/members/invite/${inviteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['members']);
    },
  });

  const handleInvite = () => {
    if (!inviteEmail.trim()) {
      setError('Email is required');
      return;
    }
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  };

  const handleUpdateRole = () => {
    if (selectedMember) {
      updateRoleMutation.mutate({
        userId: selectedMember._id,
        role: selectedMember.newRole,
      });
    }
  };

  const handleRemove = () => {
    if (selectedMember) {
      removeMutation.mutate(selectedMember._id);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const members = membersData?.members || [];
  const pendingInvites = membersData?.pendingInvites || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Team Management
          </Typography>
          <Typography variant="body1" sx={{
            color: "text.secondary"
          }}>
            Manage your tenant's team members and permissions.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => setInviteDialogOpen(true)}
        >
          Invite Member
        </Button>
      </Box>
      {/* Team Members */}
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography sx={{
                      color: "text.secondary"
                    }}>No team members yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                members.map((member) => (
                  <TableRow key={member._id}>
                    <TableCell>
                      <Typography variant="body1">{member.name || member.email}</Typography>
                      <Typography variant="body2" sx={{
                        color: "text.secondary"
                      }}>
                        {member.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.isOwner ? 'Owner' : getRoleLabel(member.role)}
                        color={member.isOwner ? 'primary' : getRoleColor(member.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {member.created ? dayjs(member.created).format('MMM D, YYYY') : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      {!member.isOwner && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedMember({ ...member, newRole: member.role });
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedMember(member);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Pending Invitations</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Sent</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingInvites.map((invite) => (
                  <TableRow key={invite._id}>
                    <TableCell>{invite.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRoleLabel(invite.role)}
                        color={getRoleColor(invite.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {dayjs(invite.date).format('MMM D, YYYY')}
                    </TableCell>
                    <TableCell>
                      {dayjs(invite.date).add(7, 'day').format('MMM D, YYYY')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        title="Cancel invitation"
                        disabled={cancelInviteMutation.isPending}
                        onClick={() => cancelInviteMutation.mutate(invite._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteRole}
              label="Role"
              onChange={(e) => setInviteRole(e.target.value)}
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box>
                    <Typography>{role.label}</Typography>
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      {role.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={inviteMutation.isPending}
          >
            {inviteMutation.isPending ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Update role for {selectedMember?.name || selectedMember?.email}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedMember?.newRole || ''}
              label="Role"
              onChange={(e) => setSelectedMember({ ...selectedMember, newRole: e.target.value })}
            >
              {ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={updateRoleMutation.isPending}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {selectedMember?.name || selectedMember?.email} from the team?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRemove}
            variant="contained"
            color="error"
            disabled={removeMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TeamManagement;
